/*
  ============================================================
  summer-cart.js — comportamento do carrinho (drawer e pagina)
  ============================================================

  O QUE FAZ:
  Abre e fecha o drawer lateral, altera quantidade e remove itens pela
  Cart AJAX API, e repinta pelo servidor. O MESMO arquivo serve o drawer
  (sections/summer-cart-drawer.liquid) e a pagina /cart
  (sections/summer-cart-page.liquid): os dois falam por data-attributes,
  e a delegacao de eventos e feita no documento.

  POR QUE PEDIR A SECTION AO SERVIDOR:
  Subtotal, barra de frete, parcelamento e upsell sao derivados do carrinho
  e ja estao escritos em Liquid. Recalcular isso em JavaScript seria manter
  duas verdades sobre o mesmo dado — e a segunda sempre diverge.

  A FILA DE QUANTIDADE (le antes de mexer):
  Clicar "+" tres vezes rapido tem que virar quantidade +3. A versao anterior
  perdia cliques: cada clique disparava um POST e a tela ficava bloqueada
  ate a resposta, descartando os cliques do meio. Agora:
    1. o input e atualizado na hora (feedback imediato, sem esperar rede);
    2. a quantidade DESEJADA vai para uma fila por linha, e cliques
       seguidos na mesma linha sobrescrevem o valor em vez de somar pedidos;
    3. um debounce curto agrupa a rajada num unico POST com o valor final;
    4. so quando a fila esvazia e a ultima resposta chega e que a repintura
       acontece — assim a tela nunca volta para um valor intermediario.
  As linhas sao identificadas por item.key, nao por indice: remover um item
  reindexa as posicoes e um POST em voo cairia na linha errada.

  CONTRATOS COM O RESTO DO TEMA (nenhum inventado aqui):
  - 'summer:cart:open'    — disparado por sections/summer-header.liquid.
                            Cancelavel: preventDefault assume a abertura.
  - 'summer:cart:changed' — disparado por assets/carrinho.js apos um
                            add-to-cart bem-sucedido.

  DEGRADACAO SEM JAVASCRIPT:
  Nada aqui e o unico caminho. Sem JS o drawer nao abre, o icone navega
  para /cart, e a pagina de carrinho continua com formulario nativo.
*/
(function () {
  'use strict';

  var ATRASO_FILA = 300;

  var drawer = null;
  var panel = null;
  var ultimoFoco = null;

  // Fila de quantidade: chave da linha -> quantidade desejada.
  var pendentes = {};
  var enviando = false;
  var precisaRepintar = false;
  var timerFila = null;

  // routes.root ja termina em barra ('/' ou '/pt-br/'), entao basta concatenar.
  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function pegarDrawer() {
    if (!drawer || !document.body.contains(drawer)) {
      drawer = document.querySelector('[data-summer-cart-drawer]');
      panel = drawer && drawer.querySelector('[data-summer-cart-panel]');
    }
    return drawer;
  }

  /* ---------------------------------------------------------
     Abrir e fechar o drawer
     --------------------------------------------------------- */
  function focaveis() {
    if (!panel) return [];
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
  }

  function abrir() {
    if (!pegarDrawer()) return;
    if (!drawer.hidden) return; // ja aberto: nao re-registrar o foco de origem

    ultimoFoco = document.activeElement;
    drawer.hidden = false;
    // Reflow antes da classe: sem isso o navegador aplica display e opacidade
    // no mesmo quadro e a transicao de entrada nao acontece.
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('summer-cart-aberto');

    var alvo = panel && panel.querySelector('[data-summer-cart-close]');
    if (alvo) alvo.focus();
  }

  function fechar() {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('summer-cart-aberto');

    var escondeu = false;
    function esconder() {
      if (escondeu) return;
      escondeu = true;
      drawer.hidden = true;
    }
    // Timeout como rede de seguranca: com prefers-reduced-motion a duracao
    // e 0 e transitionend pode nao disparar.
    if (panel) panel.addEventListener('transitionend', esconder, { once: true });
    setTimeout(esconder, 400);

    if (ultimoFoco && document.body.contains(ultimoFoco)) ultimoFoco.focus();
    ultimoFoco = null;
  }

  /* ---------------------------------------------------------
     Repintura pelo servidor — drawer e pagina de uma vez
     --------------------------------------------------------- */
  function containers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-summer-cart-section]'));
  }

  /* A repintura pede a RAIZ da loja de proposito, e nao a pagina atual.
     Testado no dev server: o id da pagina de carrinho chega ao cliente como
     `template--<id>__main` (a Shopify prefixa os ids vindos de template JSON),
     e esse id e enderecavel de qualquer url — pedir a raiz devolve o drawer E
     a pagina de carrinho, os dois com o conteudo certo.

     NAO troque por window.location.pathname: em /cart, a rota responde ao
     header `Accept: application/json` abaixo com o objeto do carrinho em vez
     das sections, e a repintura para de achar as duas chaves. A raiz nao tem
     esse comportamento. */

  function percentualDe(el) {
    var fill = el.querySelector('[data-summer-wave-fill]');
    return fill ? fill.style.getPropertyValue('--summer-wave-fill') : null;
  }

  /* A barra so "enche" se sair de algum lugar. Como o HTML novo ja chega com
     o percentual final, ela apareceria cheia de imediato. Entao: aplica o
     percentual ANTIGO, forca reflow e devolve o novo — a transicao de width
     em summer-components.css faz o resto. */
  function animarBarra(el, anterior) {
    var fill = el.querySelector('[data-summer-wave-fill]');
    if (!fill || anterior === null || anterior === '') return;

    var destino = fill.style.getPropertyValue('--summer-wave-fill');
    if (destino === anterior) return;

    fill.style.setProperty('--summer-wave-fill', anterior);
    void fill.offsetWidth;
    fill.style.setProperty('--summer-wave-fill', destino);
  }

  function atualizarContador(total) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = total;
    });
  }

  /* SUMMER: aplicar() separado de repintar().

     O HTML das sections passou a chegar por DOIS caminhos: o de sempre (uma
     requisicao propria, quando quem mudou o carrinho foi a fila de quantidade)
     e o novo, em que assets/carrinho.js ja traz as sections na resposta do
     proprio /cart/add.js e as entrega no detail do evento. A troca no DOM e
     identica nos dois; so a origem do objeto muda. Duas copias dessa troca
     seriam duas chances de divergir. */
  function aplicar(dados) {
    var alvos = containers();
    var anteriores = alvos.map(percentualDe);

    alvos.forEach(function (el, i) {
      var html = dados[el.getAttribute('data-summer-cart-section')];
      if (!html) return;

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var novo = doc.querySelector('[data-summer-cart-refresh]');
      var atual = el.querySelector('[data-summer-cart-refresh]');
      if (!novo || !atual) return;

      atual.innerHTML = novo.innerHTML;
      animarBarra(el, anteriores[i]);

      var novoRaiz = doc.querySelector('[data-summer-cart-section]');
      var total = novoRaiz && novoRaiz.getAttribute('data-summer-cart-total');
      if (total !== null && total !== undefined) {
        el.setAttribute('data-summer-cart-total', total);
        atualizarContador(total);
      }
    });
  }

  function repintar() {
    var alvos = containers();
    if (!alvos.length) return Promise.resolve();

    var ids = alvos.map(function (el) { return el.getAttribute('data-summer-cart-section'); });
    alvos.forEach(function (el) { el.setAttribute('aria-busy', 'true'); });

    return fetch(rootUrl() + '?sections=' + ids.join(','), {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('section fetch failed');
        return r.json();
      })
      .then(function (dados) {
        aplicar(dados);
      })
      .catch(function () {
        // O carrinho no servidor ja mudou, entao a tela aberta esta mentindo.
        // Ir para /cart e o caminho honesto.
        window.location.href = rootUrl() + 'cart';
      })
      .finally(function () {
        containers().forEach(function (el) { el.removeAttribute('aria-busy'); });
      });
  }

  /* ---------------------------------------------------------
     Fila de alteracao de quantidade
     --------------------------------------------------------- */
  function processarFila() {
    if (enviando) return;

    var chaves = Object.keys(pendentes);
    if (!chaves.length) {
      // Fila vazia: agora sim vale repintar, uma vez so.
      if (precisaRepintar) {
        precisaRepintar = false;
        repintar();
      }
      return;
    }

    var chave = chaves[0];
    var quantidade = pendentes[chave];
    delete pendentes[chave];

    enviando = true;
    precisaRepintar = true;

    fetch(rootUrl() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      // 'id' com a key da linha, e nao 'line' com o indice: imune a
      // reindexacao quando outra linha e removida.
      body: JSON.stringify({ id: chave, quantity: quantidade })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change failed');
        return r.json();
      })
      .catch(function () {
        var texto = window.themeStrings && window.themeStrings.cartError;
        if (texto && typeof window.mostrarAlerta === 'function') {
          window.mostrarAlerta(texto, 'negativo');
        }
        // Repintar mesmo assim: a tela precisa voltar para o que o servidor
        // realmente tem, senao fica exibindo o valor otimista que falhou.
        precisaRepintar = true;
      })
      .finally(function () {
        enviando = false;
        processarFila();
      });
  }

  function agendarQuantidade(chave, quantidade, imediato) {
    pendentes[chave] = quantidade;
    clearTimeout(timerFila);
    if (imediato) {
      processarFila();
    } else {
      timerFila = setTimeout(processarFila, ATRASO_FILA);
    }
  }

  /* Feedback otimista: o numero muda no mesmo quadro do clique. Sem isso a
     interface parece travada durante a ida ao servidor, que e justamente o
     que fazia o cliente clicar de novo. */
  function refletirQuantidade(chave, quantidade) {
    document
      .querySelectorAll('[data-summer-cart-line="' + (window.CSS && CSS.escape ? CSS.escape(chave) : chave) + '"] [data-summer-cart-quantity]')
      .forEach(function (input) { input.value = quantidade; });
  }

  function lerQuantidade(linhaEl) {
    var input = linhaEl.querySelector('[data-summer-cart-quantity]');
    var valor = input ? parseInt(input.value, 10) : 0;
    return isNaN(valor) ? 0 : valor;
  }

  function ajustar(linhaEl, delta) {
    var chave = linhaEl.getAttribute('data-summer-cart-line');
    // O valor de partida vem da fila quando ha rajada em curso — ler do input
    // funcionaria igual, ja que ele e atualizado na hora, mas a fila e a
    // fonte da verdade do que ainda sera enviado.
    var base = pendentes.hasOwnProperty(chave) ? pendentes[chave] : lerQuantidade(linhaEl);
    var alvo = Math.max(0, base + delta);
    refletirQuantidade(chave, alvo);
    // Chegar a zero e remocao: vai na hora, sem esperar o debounce.
    agendarQuantidade(chave, alvo, alvo === 0);
  }

  /* ---------------------------------------------------------
     Delegacao de eventos
     Ouvir no documento (e nao nos botoes) e o que mantem tudo funcionando
     depois de cada repintura, sem reanexar listener nenhum.
     --------------------------------------------------------- */
  document.addEventListener('click', function (event) {
    var alvo = event.target;

    if (alvo.closest('[data-summer-cart-close]') || alvo.closest('[data-summer-cart-overlay]')) {
      event.preventDefault();
      fechar();
      return;
    }

    var linhaEl = alvo.closest('[data-summer-cart-line]');
    if (!linhaEl) return;

    if (alvo.closest('[data-summer-cart-remove]')) {
      event.preventDefault();
      var chave = linhaEl.getAttribute('data-summer-cart-line');
      refletirQuantidade(chave, 0);
      agendarQuantidade(chave, 0, true);
    } else if (alvo.closest('[data-summer-cart-plus]')) {
      event.preventDefault();
      ajustar(linhaEl, 1);
    } else if (alvo.closest('[data-summer-cart-minus]')) {
      event.preventDefault();
      ajustar(linhaEl, -1);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-summer-cart-quantity]');
    if (!input) return;
    var linhaEl = input.closest('[data-summer-cart-line]');
    if (!linhaEl) return;

    var quantidade = parseInt(input.value, 10);
    if (isNaN(quantidade) || quantidade < 0) quantidade = 0;
    input.value = quantidade;
    agendarQuantidade(linhaEl.getAttribute('data-summer-cart-line'), quantidade, true);
  });

  document.addEventListener('keydown', function (event) {
    if (!drawer || drawer.hidden) return;

    if (event.key === 'Escape') {
      fechar();
      return;
    }

    // Foco preso: um dialogo modal que deixa o Tab escapar para a pagina
    // atras dele nao e modal para quem navega por teclado.
    if (event.key !== 'Tab') return;
    var lista = focaveis();
    if (!lista.length) return;
    var primeiro = lista[0];
    var ultimo = lista[lista.length - 1];

    if (event.shiftKey && document.activeElement === primeiro) {
      event.preventDefault();
      ultimo.focus();
    } else if (!event.shiftKey && document.activeElement === ultimo) {
      event.preventDefault();
      primeiro.focus();
    }
  });

  /* ---------------------------------------------------------
     Contratos com header e carrinho.js
     --------------------------------------------------------- */
  document.addEventListener('summer:cart:open', function (event) {
    if (!pegarDrawer()) return;
    // preventDefault avisa o header que o drawer assumiu; sem isso o link
    // segue para /cart, que e exatamente o fallback desejado.
    event.preventDefault();
    abrir();
  });

  document.addEventListener('summer:cart:changed', function (event) {
    if (!containers().length) return;

    /* Sections ja vieram na resposta do add: aplica na hora, sem segunda
       viagem. Sem elas (fila de quantidade, remocao), pede como sempre. */
    var prontas = event && event.detail && event.detail.sections;
    var trabalho;
    if (prontas) {
      try {
        aplicar(prontas);
        trabalho = Promise.resolve();
      } catch (e) {
        trabalho = repintar();
      }
    } else {
      trabalho = repintar();
    }

    trabalho
      .then(function () {
        // Na pagina /cart o conteudo ja esta a vista: abrir o drawer por cima
        // seria empilhar duas versoes da mesma coisa. Se o drawer ja tiver
        // sido aberto no clique (ver carrinho.js), abrir() e no-op.
        if (window.location.pathname.indexOf('/cart') === -1) abrir();
      })
      .finally(function () {
        // Solta o rodape travado durante a espera — vale tanto para o caminho
        // que abriu no clique quanto para o que pediu as sections aqui.
        containers().forEach(function (el) { el.removeAttribute('aria-busy'); });
      });
  });

  document.addEventListener('DOMContentLoaded', pegarDrawer);
})();
